"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SubjectCard = void 0;
const tslib_1 = require("tslib");
const react_1 = tslib_1.__importDefault(require("react"));
const Email_1 = tslib_1.__importDefault(require("@mui/icons-material/Email"));
const Phone_1 = tslib_1.__importDefault(require("@mui/icons-material/Phone"));
const material_1 = require("@mui/material");
const ListItemAvatar_1 = tslib_1.__importDefault(require("@mui/material/ListItemAvatar"));
const ListItemText_1 = tslib_1.__importDefault(require("@mui/material/ListItemText"));
const pure_utils_1 = require("@mnemonic/pure-utils");
const SubjectAvatar_1 = require("../SubjectAvatar");
const CoreContext_1 = require("../_InternalComponents/CoreContext");
const isDomainDifferent_1 = require("../_InternalUtils/isDomainDifferent");
/**
 *
 * A card used to display subject information, e. g in a Popover.
 * Subject is assumed to be a user if type is not "group" and "daemonAccount" is not included in flags.
 *
 * @remarks
 * ## includeUserDetails
 * The defails are only displayed if the subject is a user, as this information is not available for groups or user agents
 *
 * ## extendedSection
 * The extendedSection is meant to be as flexible as possible and will render what ever is sent in with a grey background
 *
 * @example ### Basic example
 *
 * This examples shows a basic rendering of this component
 *
 * ```jsx live noInline
 * file://./__examples__/1_SubjectCard.jsx
 * ```
 * @category component
 */
const SubjectCard = ({ subject, includeUserDetails = false, extendedSection, sx, ...rest }) => {
    const contextDomain = react_1.default.useContext(CoreContext_1.CoreContext)?.domain;
    const isUserAgent = subject?.flags?.includes("daemonAccount");
    const isGroup = subject?.type === "group";
    const isUser = !isUserAgent && !isGroup;
    return (react_1.default.createElement(material_1.Card, { elevation: 9, ...rest, sx: [
            {
                maxWidth: "500px",
                height: "fit-content",
            },
            ...(Array.isArray(sx) ? sx : [sx]),
        ] },
        react_1.default.createElement(material_1.List, { dense: true },
            react_1.default.createElement(material_1.ListItem, { dense: true },
                react_1.default.createElement(ListItemAvatar_1.default, null,
                    react_1.default.createElement(SubjectAvatar_1.SubjectAvatar, { subject: subject })),
                react_1.default.createElement(ListItemText_1.default, { primary: react_1.default.createElement(material_1.Typography, { variant: "h6", noWrap: true }, subject?.name), secondary: react_1.default.createElement(material_1.Typography, { noWrap: true, variant: "body2", color: "textSecondary" },
                        (0, isDomainDifferent_1.isDomainDifferent)(contextDomain, subject?.domain) && `${subject.domain.name}/`,
                        subject?.shortName) })),
            react_1.default.createElement(ListItemText_1.default, { inset: true, primary: react_1.default.createElement(material_1.ListItem, { dense: true, sx: {
                        /* Small crime/hack to make the component look more like the sketches (better than implementing with Grid + more proprietary css) */
                        marginTop: (theme) => theme.spacing(-2),
                    } },
                    react_1.default.createElement(material_1.Typography, { variant: "body1", noWrap: true }, subject?.customer?.name)) }),
            includeUserDetails && isUser && (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement(ListItemText_1.default, { inset: true, primary: react_1.default.createElement(material_1.ListItem, { dense: true },
                        react_1.default.createElement(material_1.ListItemIcon, { sx: { minWidth: 0, marginRight: 1 } },
                            react_1.default.createElement(Email_1.default, null)),
                        react_1.default.createElement(material_1.Typography, { variant: "body1", noWrap: true }, subject?.email || "N/A")) }),
                react_1.default.createElement(ListItemText_1.default, { inset: true, primary: react_1.default.createElement(material_1.ListItem, { dense: true },
                        react_1.default.createElement(material_1.ListItemIcon, { sx: { minWidth: 0, marginRight: 1 } },
                            react_1.default.createElement(Phone_1.default, null)),
                        react_1.default.createElement(material_1.Typography, { variant: "body1", noWrap: true }, (0, pure_utils_1.formattedPhoneNumber)(subject?.phone))) })))),
        extendedSection && react_1.default.createElement(material_1.Box, { bgcolor: (theme) => theme.palette.grey[200] }, extendedSection)));
};
exports.SubjectCard = SubjectCard;
